import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Grid from '@precis-digital/kurama/Grid';
import MaximizeIcon from '@precis-digital/kurama/icons/MaximizeIcon';
import MinimizeIcon from '@precis-digital/kurama/icons/MinimizeIcon';
import { RechartsResponsiveContainer as ResponsiveContainer } from '@precis-digital/kurama/recharts';
import Skeleton from '@precis-digital/kurama/Skeleton';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import { type ReactElement, type ReactNode, useCallback, useRef } from 'react';
import { chartAllowsZooming } from '../constants';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
import useChartChoicesElements from '../hooks/useChartChoicesElements';
import usePrepareData from '../hooks/usePrepareData';
import type { ChangeCardType, ChartCardChartContainerProps, ChartMarginsAndAxisSpaces } from '../internalTypes';
import type { ChartCardActionContext, ChartCardChartActionContext, ChartCardCustomisationsProps, ChartCardType, ChartDataRow, ViewType } from '../types';
import { getDefaultChartMarginsAndAxisSpaces } from '../utils/axis';
import { getActionClickHandler, getChartCardChartContainerSize } from '../utils/card';
import { exportChartAsSVGOrPNG } from '../utils/chart';
import Chart from './Chart';
import ChartCardMoreMenu from './ChartCardMoreMenu';
import ChartDevtoolsWrapper from './ChartDevToolsWrapper';
import IconTemplates from './IconTemplates';
import ChartTableSwap from './styled/ChartTableSwap';
import StyledCard from './styled/StyledCard';
import StyledTypographyNoWrap from './styled/StyledTypographyNoWrap';
const ChartCardChartContainer = props => {
  const $ = _c(123);
  const {
    cardSize,
    chartSize,
    title,
    downloadTitle,
    actions,
    moreMenuActions,
    expandedToggle,
    useDebugTools,
    allowResetCard,
    allowExport,
    tooltipContent,
    dimensionsMap,
    metricsVariantMap,
    dimensionsValuesMap,
    chart,
    respectColors,
    axisDrawing,
    chartDrawing,
    drawingColorMaps,
    elementsDescriptionsMap,
    state,
    dispatch,
    otherCardTypes,
    changeCardType,
    t
  } = props;
  const allowZooming = chartAllowsZooming[chart.type] && (props.allowZooming ?? false);
  const {
    choicesList,
    choicesMap,
    selections,
    selectionsUpdated,
    zoomedIn,
    firstAxisDomain,
    secondAxisDomains
  } = state;
  const {
    processedData,
    newMetricsMap,
    transposedMetricsLists
  } = usePrepareData(props);
  let t0;
  if ($[0] !== chart.choicesTogether || $[1] !== choicesList || $[2] !== choicesMap || $[3] !== dispatch || $[4] !== selections || $[5] !== t || $[6] !== transposedMetricsLists) {
    t0 = {
      useChips: true,
      choicesList,
      choicesMap,
      choicesTogether: chart.choicesTogether,
      transposedMetricsLists,
      selections,
      dispatch,
      t
    };
    $[0] = chart.choicesTogether;
    $[1] = choicesList;
    $[2] = choicesMap;
    $[3] = dispatch;
    $[4] = selections;
    $[5] = t;
    $[6] = transposedMetricsLists;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  const {
    singleElements,
    multipleElements,
    togetherElements
  } = useChartChoicesElements(t0);
  const containerRef = useRef(null);
  let t1;
  if ($[8] !== chart) {
    t1 = getDefaultChartMarginsAndAxisSpaces(chart);
    $[8] = chart;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const chartMarginRef = useRef(t1);
  const defaults = useChartCardDefaults();
  const theme = useTheme();
  let t2;
  if ($[10] !== chart.choicesTogether || $[11] !== choicesList || $[12] !== choicesMap || $[13] !== defaults || $[14] !== downloadTitle || $[15] !== selections || $[16] !== t || $[17] !== theme || $[18] !== title || $[19] !== transposedMetricsLists) {
    t2 = async (exportAs, copyToClipboard) => {
      await exportChartAsSVGOrPNG({
        defaults,
        theme,
        choicesList,
        choicesMap,
        choicesTogether: chart.choicesTogether,
        transposedMetricsLists,
        selections,
        containerRef,
        chartMarginRef,
        title,
        downloadFileName: downloadTitle ?? title,
        t,
        exportAs,
        copyToClipboard
      });
    };
    $[10] = chart.choicesTogether;
    $[11] = choicesList;
    $[12] = choicesMap;
    $[13] = defaults;
    $[14] = downloadTitle;
    $[15] = selections;
    $[16] = t;
    $[17] = theme;
    $[18] = title;
    $[19] = transposedMetricsLists;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  const handleExport = t2;
  let t3;
  if ($[21] !== processedData) {
    t3 = () => ({
      type: "chart",
      data: processedData
    });
    $[21] = processedData;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const getContext = t3;
  let t4;
  let t5;
  if ($[23] !== dispatch) {
    t4 = () => {
      dispatch({
        type: "resetSelections"
      });
    };
    t5 = () => {
      dispatch({
        type: "resetZooming"
      });
    };
    $[23] = dispatch;
    $[24] = t4;
    $[25] = t5;
  } else {
    t4 = $[24];
    t5 = $[25];
  }
  let t6;
  if ($[26] !== allowExport || $[27] !== allowResetCard || $[28] !== allowZooming || $[29] !== changeCardType || $[30] !== chart.view || $[31] !== expandedToggle || $[32] !== getContext || $[33] !== handleExport || $[34] !== moreMenuActions || $[35] !== otherCardTypes || $[36] !== selectionsUpdated || $[37] !== t || $[38] !== t4 || $[39] !== t5 || $[40] !== zoomedIn) {
    t6 = <ChartCardMoreMenu view={chart.view} moreMenuActions={moreMenuActions} expandedToggle={expandedToggle} handleExport={handleExport} needsResetCard={selectionsUpdated} allowResetCard={allowResetCard} allowExport={allowExport} allowZooming={allowZooming} cardType="chart" otherCardTypes={otherCardTypes} changeCardType={changeCardType} handleResetCard={t4} handleResetZooming={t5} zoomedIn={zoomedIn} getContext={getContext} t={t} />;
    $[26] = allowExport;
    $[27] = allowResetCard;
    $[28] = allowZooming;
    $[29] = changeCardType;
    $[30] = chart.view;
    $[31] = expandedToggle;
    $[32] = getContext;
    $[33] = handleExport;
    $[34] = moreMenuActions;
    $[35] = otherCardTypes;
    $[36] = selectionsUpdated;
    $[37] = t;
    $[38] = t4;
    $[39] = t5;
    $[40] = zoomedIn;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  const moreMenu = t6;
  let t7;
  if ($[42] !== actions || $[43] !== changeCardType || $[44] !== expandedToggle || $[45] !== getContext || $[46] !== otherCardTypes) {
    t7 = getButtons({
      getContext,
      otherCardTypes,
      changeCardType,
      actions,
      expandedToggle
    });
    $[42] = actions;
    $[43] = changeCardType;
    $[44] = expandedToggle;
    $[45] = getContext;
    $[46] = otherCardTypes;
    $[47] = t7;
  } else {
    t7 = $[47];
  }
  const buttons = t7;
  let t8;
  if ($[48] !== cardSize || $[49] !== chart.view || $[50] !== chartSize) {
    t8 = getChartCardChartContainerSize({
      cardSize,
      chartSize,
      view: chart.view
    });
    $[48] = cardSize;
    $[49] = chart.view;
    $[50] = chartSize;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  const {
    size,
    cardMinWidth,
    containerMinWidth,
    containerMinHeight,
    containerWidth,
    containerHeight,
    chartWidth,
    chartHeight
  } = t8;
  let t9;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IconTemplates />;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  let t10;
  if ($[53] !== theme) {
    t10 = theme.spacing(2);
    $[53] = theme;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  let t11;
  if ($[55] !== theme) {
    t11 = theme.spacing(3);
    $[55] = theme;
    $[56] = t11;
  } else {
    t11 = $[56];
  }
  let t12;
  if ($[57] !== theme) {
    t12 = theme.spacing(2);
    $[57] = theme;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  let t13;
  if ($[59] !== title) {
    t13 = <StyledTypographyNoWrap variant="h3">{title}</StyledTypographyNoWrap>;
    $[59] = title;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== singleElements || $[62] !== t12 || $[63] !== t13) {
    t14 = <Grid item={true} container={true} alignItems="center" gap={t12}>{t13}{singleElements}</Grid>;
    $[61] = singleElements;
    $[62] = t12;
    $[63] = t13;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== theme) {
    t15 = theme.spacing(1);
    $[65] = theme;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== buttons || $[68] !== moreMenu || $[69] !== t15) {
    t16 = <Grid display="flex" gap={t15}>{buttons}{moreMenu}</Grid>;
    $[67] = buttons;
    $[68] = moreMenu;
    $[69] = t15;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] !== t11 || $[72] !== t14 || $[73] !== t16) {
    t17 = <Grid display="flex" justifyContent="space-between" gap={t11} width="100%" alignItems="center">{t14}{t16}</Grid>;
    $[71] = t11;
    $[72] = t14;
    $[73] = t16;
    $[74] = t17;
  } else {
    t17 = $[74];
  }
  let t18;
  if ($[75] !== togetherElements) {
    t18 = Array.from(togetherElements.entries()).map(_temp);
    $[75] = togetherElements;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  let t19;
  if ($[77] !== multipleElements || $[78] !== t18) {
    t19 = <StyledGridTopLegendContainer>{multipleElements}{t18}</StyledGridTopLegendContainer>;
    $[77] = multipleElements;
    $[78] = t18;
    $[79] = t19;
  } else {
    t19 = $[79];
  }
  let t20;
  if ($[80] !== t10 || $[81] !== t17 || $[82] !== t19) {
    t20 = <Grid display="flex" flexDirection="column" gap={t10} width="100%">{t17}{t19}</Grid>;
    $[80] = t10;
    $[81] = t17;
    $[82] = t19;
    $[83] = t20;
  } else {
    t20 = $[83];
  }
  const t21 = respectColors ?? false;
  let t22;
  if ($[84] !== allowZooming || $[85] !== axisDrawing || $[86] !== chart || $[87] !== chartDrawing || $[88] !== chartHeight || $[89] !== chartWidth || $[90] !== choicesList || $[91] !== choicesMap || $[92] !== dimensionsMap || $[93] !== dimensionsValuesMap || $[94] !== dispatch || $[95] !== drawingColorMaps || $[96] !== elementsDescriptionsMap || $[97] !== firstAxisDomain || $[98] !== metricsVariantMap || $[99] !== newMetricsMap || $[100] !== processedData || $[101] !== secondAxisDomains || $[102] !== selections || $[103] !== size || $[104] !== t || $[105] !== t21 || $[106] !== tooltipContent || $[107] !== transposedMetricsLists || $[108] !== zoomedIn) {
    t22 = <Chart chartMarginRef={chartMarginRef} containerRef={containerRef} size={size} width={chartWidth} height={chartHeight} tooltipContent={tooltipContent} data={processedData} dimensionsMap={dimensionsMap} metricsVariantMap={metricsVariantMap} metricsMap={newMetricsMap} transposedMetricsLists={transposedMetricsLists} dimensionsValuesMap={dimensionsValuesMap} chart={chart} respectColors={t21} axisDrawing={axisDrawing} chartDrawing={chartDrawing} drawingColorMaps={drawingColorMaps} selections={selections} choicesList={choicesList} choicesMap={choicesMap} elementsDescriptionsMap={elementsDescriptionsMap} allowZooming={allowZooming} zoomedIn={zoomedIn} dispatch={dispatch} t={t} firstAxisDomain={firstAxisDomain} secondAxisDomains={secondAxisDomains} />;
    $[84] = allowZooming;
    $[85] = axisDrawing;
    $[86] = chart;
    $[87] = chartDrawing;
    $[88] = chartHeight;
    $[89] = chartWidth;
    $[90] = choicesList;
    $[91] = choicesMap;
    $[92] = dimensionsMap;
    $[93] = dimensionsValuesMap;
    $[94] = dispatch;
    $[95] = drawingColorMaps;
    $[96] = elementsDescriptionsMap;
    $[97] = firstAxisDomain;
    $[98] = metricsVariantMap;
    $[99] = newMetricsMap;
    $[100] = processedData;
    $[101] = secondAxisDomains;
    $[102] = selections;
    $[103] = size;
    $[104] = t;
    $[105] = t21;
    $[106] = tooltipContent;
    $[107] = transposedMetricsLists;
    $[108] = zoomedIn;
    $[109] = t22;
  } else {
    t22 = $[109];
  }
  let t23;
  if ($[110] !== containerHeight || $[111] !== containerMinHeight || $[112] !== containerMinWidth || $[113] !== containerWidth || $[114] !== t22) {
    t23 = <ResponsiveContainer ref={containerRef} width={containerWidth} height={containerHeight} minWidth={containerMinWidth} minHeight={containerMinHeight}>{t22}</ResponsiveContainer>;
    $[110] = containerHeight;
    $[111] = containerMinHeight;
    $[112] = containerMinWidth;
    $[113] = containerWidth;
    $[114] = t22;
    $[115] = t23;
  } else {
    t23 = $[115];
  }
  let t24;
  if ($[116] !== t23 || $[117] !== useDebugTools) {
    t24 = <ChartDevtoolsWrapper useDebugTools={useDebugTools}>{t23}</ChartDevtoolsWrapper>;
    $[116] = t23;
    $[117] = useDebugTools;
    $[118] = t24;
  } else {
    t24 = $[118];
  }
  let t25;
  if ($[119] !== cardMinWidth || $[120] !== t20 || $[121] !== t24) {
    t25 = <StyledCard minWidth={cardMinWidth}>{t9}{t20}{t24}</StyledCard>;
    $[119] = cardMinWidth;
    $[120] = t20;
    $[121] = t24;
    $[122] = t25;
  } else {
    t25 = $[122];
  }
  return t25;
};
export const ChartCardChartContainerSkeleton = t0 => {
  const $ = _c(9);
  const {
    cardSize,
    chartSize,
    view
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== cardSize || $[1] !== chartSize || $[2] !== view) {
    t1 = getChartCardChartContainerSize({
      cardSize,
      chartSize,
      view
    });
    $[0] = cardSize;
    $[1] = chartSize;
    $[2] = view;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    containerWidth,
    containerHeight
  } = t1;
  const skeletonWidth = containerWidth;
  let t2;
  if ($[4] !== theme) {
    t2 = theme.spacing(8);
    $[4] = theme;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const skeletonHeight = `calc(${containerHeight}px + ${t2})`;
  let t3;
  if ($[6] !== skeletonHeight || $[7] !== skeletonWidth) {
    t3 = <Skeleton width={skeletonWidth} height={skeletonHeight} variant="rectangular" />;
    $[6] = skeletonHeight;
    $[7] = skeletonWidth;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const getButtons = <DataKey extends string = string, DataRow extends ChartDataRow<DataKey> = ChartDataRow<DataKey>>({
  getContext,
  otherCardTypes,
  changeCardType,
  actions,
  expandedToggle
}: {
  getContext: () => ChartCardActionContext<DataKey, DataRow> | null;
  otherCardTypes: ChartCardType[];
  changeCardType: ChangeCardType;
  actions: ChartCardChartContainerProps<DataKey, DataRow>['actions'];
  expandedToggle: ChartCardChartContainerProps<DataKey, DataRow>['expandedToggle'];
}): ReactNode => {
  const buttons: ReactElement[] = [];
  if (expandedToggle != null) {
    const leftIcon = expandedToggle.expanded ? <MinimizeIcon /> : <MaximizeIcon />;
    buttons.push(<Button key="expandToggle" onClick={() => {
      expandedToggle.toggle();
    }} variant="text" leftIcon={leftIcon} />);
  }
  if (actions != null) {
    for (const {
      id,
      label,
      icon,
      onClick,
      Component
    } of actions) {
      if (Component == null) {
        buttons.push(<Button key={id} onClick={getActionClickHandler(id, onClick, getContext)} variant="text" leftIcon={icon}>
            {icon == null ? label ?? id : label}
          </Button>);
      } else {
        buttons.push(<Component key={id} id={id} getContext={getContext} />);
      }
    }
  }
  if (otherCardTypes.length > 0) {
    buttons.push(<ChartTableSwap changeCardType={changeCardType} cardType="chart" otherCardTypes={otherCardTypes} />);
  }
  return buttons;
};
const StyledGridTopLegendContainer = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(0),
  flexDirection: 'column',
  borderRadius: theme.borderRadius.medium,
  alignItems: 'left',
  justifyContent: 'start',
  width: '100%',
  height: 'fit-content',
  padding: theme.spacing(0)
}));
const StyledTogetherElementsGrid = styled(Grid)(({
  theme
}) => ({
  padding: theme.spacing(0),
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0),
  width: 'fit-content'
}));
export default ChartCardChartContainer;
function _temp(t0) {
  const [key, elements] = t0;
  return <StyledTogetherElementsGrid key={key}>{elements}</StyledTogetherElementsGrid>;
}